import React from 'react';
import classNames from 'classnames';

interface RadioOption {
	label: string;
	value: string;
	/** A second, quieter line under the label. */
	description?: string;
	testId?: string;
}

const sizeClasses = {
	sm: { outer: 'w-5 h-5 border', inner: 'w-3 h-3', gap: 'gap-2', label: 'text-sm', gapList: 'gap-2' },
	md: { outer: 'w-9 h-9 border-2', inner: 'w-6 h-6', gap: 'gap-3', label: '', gapList: 'gap-4' },
} as const;

interface RadioInputProps {
	label?: string;
	required?: boolean;
	options: RadioOption[];
	value?: string;
	onChange?: (value: string) => void;
	orientation?: 'vertical' | 'horizontal';
	className?: string;
	disabled?: boolean;
	size?: 'sm' | 'md';
}

export const RadioInput: React.FC<RadioInputProps> = ({
	label,
	required,
	options,
	value,
	onChange,
	orientation = 'vertical',
	className,
	disabled,
	size = 'md',
}) => {
	const s = sizeClasses[size];
	return (
		<div className={classNames('flex flex-col gap-2', className)}>
			{label && (
				<span className={classNames('text-sm', disabled ? 'text-gray-400' : 'text-gray-800')}>
					{label} {required && <span className="text-[var(--colour-brand-primary)]">*</span>}
				</span>
			)}
			<div
				className={classNames(
					'flex',
					s.gapList,
					orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap',
				)}
			>
				{options.map(option => (
					<label
						key={option.value}
						className={classNames(
							'relative flex group',
							option.description ? 'items-start' : 'items-center',
							s.gap,
							disabled ? 'cursor-not-allowed' : 'cursor-pointer',
						)}
					>
						<div className="relative flex items-center justify-center">
							<input
								type="radio"
								className="absolute opacity-0 pointer-events-none"
								checked={value === option.value}
								onChange={() => !disabled && onChange?.(option.value)}
								disabled={disabled}
								data-test-id={option.testId}
							/>
							<div
								className={classNames(
									'rounded-full transition-colors flex items-center justify-center flex-shrink-0',
									s.outer,
									// eslint-disable-next-line no-nested-ternary
									value === option.value
										? disabled
											? 'border-gray-300 bg-gray-50'
											: 'border-[#0180B5]'
										: disabled
											? 'border-gray-300 bg-gray-50'
											: 'border-gray-300 bg-white group-hover:border-cyan-400',
								)}
							>
								{value === option.value && (
									<div
										className={classNames(
											'rounded-full',
											s.inner,
											disabled ? 'bg-gray-300' : 'bg-[#0180B5]',
										)}
									/>
								)}
							</div>
						</div>
						<span className={classNames('flex flex-col', s.label)}>
							<span className={classNames(disabled ? 'text-gray-400' : 'text-gray-800')}>
								{option.label}
							</span>
							{option.description && <span className="text-xs text-gray-500">{option.description}</span>}
						</span>
					</label>
				))}
			</div>
		</div>
	);
};
